import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        setToken(null);
        setUser(null);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    let response;
    try {
      response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });
    } catch (e) {
      throw new Error('Eroare de rețea');
    }

    let data;
    try {
      data = await response.json();
    } catch (e) {
      if (!response.ok) {
        throw new Error('Credențiale invalide');
      }
      throw new Error('Eroare la autentificare');
    }

    if (!response.ok) {
      throw new Error(data?.detail || 'Credențiale invalide');
    }

    localStorage.setItem('auth_token', data.access_token);
    localStorage.setItem('refresh_token', data.refresh_token);
    setToken(data.access_token);
    setUser(data.user);

    return data;
  };

  const register = async (username, email, password) => {
    const response = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ username, email, password })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || 'Eroare la înregistrare');
    }

    return data;
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    setToken(null);
    setUser(null);
  };

  const googleLogin = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processGoogleCallback = async (sessionId) => {
    const response = await fetch(`${API_URL}/api/auth/google/session`, {
      headers: {
        'X-Session-ID': sessionId
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || 'Eroare la autentificare Google');
    }

    localStorage.setItem('auth_token', data.access_token);
    setToken(data.access_token);
    setUser(data.user);

    return data;
  };

  const forgotPassword = async (email) => {
    const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || 'Eroare');
    }

    return data;
  };

  const resetPassword = async (resetToken, newPassword) => {
    const response = await fetch(`${API_URL}/api/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ token: resetToken, password: newPassword })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || 'Eroare la resetarea parolei');
    }

    return data;
  };

  // SSO: Validate token from another app and set session
  const validateAndSetToken = async (ssoToken) => {
    try {
      // First, try to use the token to get user info
      const response = await fetch(`${API_URL}/api/me`, {
        headers: {
          'Authorization': `Bearer ${ssoToken}`
        }
      });

      if (response.ok) {
        const userData = await response.json();
        // Token is valid, save it
        localStorage.setItem('auth_token', ssoToken);
        setToken(ssoToken);
        setUser(userData);
        return userData;
      } else {
        throw new Error('Token invalid');
      }
    } catch (error) {
      console.error('SSO token validation failed:', error);
      throw error;
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    googleLogin,
    processGoogleCallback,
    forgotPassword,
    resetPassword,
    validateAndSetToken,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.role === 'moderator'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
